/**
 * session-builder.ts
 *
 * Shared `createAgentSession()` factory — extracts the common agent
 * initialisation sequence from agent.ts (REPL), run-flow.ts (flow), and
 * serve.ts (WebSocket server) into a single reusable module.
 *
 * The shared setup pipeline:
 *   1. resolveSettings → driverType, provider, model, apiKey
 *   2. loadAgentManifest → manifest (model, maxTurns, tools, name)
 *   3. buildAgentResources → resourceManager, resourcePromptSection, mcpServers, dispose
 *   4. Load workspace config, build contextSection + environmentSection, load prompt-extensions
 *   5. assembleSystemPrompt (context + env + resources + extensions + additional sections)
 *   6. Resolve maxTurns (config override wins over manifest)
 *   7. createDriver (NOT started — caller calls driver.start() when ready)
 *
 * Agent identity (SOUL.md, RULES.md, knowledge/) is never assembled here.
 * It is rendered at install time by the renderers in asset-manager and read
 * natively by each backend driver at runtime.
 */

import { prepareNativePolicy } from "../../bridge/src/codex/native-policy.js";
import { readFile } from "node:fs/promises";
import { randomUUID } from "node:crypto";
import { resolve as resolvePath } from "node:path";
import type {
  AgentDriver,
  BridgeCapabilityHooks,
  CodexManagedRuntimeOptions,
  CodexDriverOptions,
} from "@skaile/workspaces/bridge";
import { createDriver } from "@skaile/workspaces/bridge";
import type {
  AgentManifest,
  RuntimeAssetsResult,
  SkWorkspaceConfig,
} from "@skaile/workspaces/core";
import {
  resolveApiKey,
  resolveSettings,
  resolveSkWorkspaceConfig,
  withFsDeadline,
} from "@skaile/workspaces/core";
import { createLogger } from "@skaile/workspaces/core/logging";
import type { TelemetryProvider, Trace } from "@skaile/workspaces/telemetry";
import type { SystemPromptSection } from "@skaile/workspaces/types";
import type { CapabilityRegistry } from "./capability-registry.js";
import { type ResolvedComposition, resolveAgentComposition } from "./composition/bootstrap.js";
import { buildContextSection } from "./context.js";
import { buildEnvironmentSection } from "./environment.js";
import { prepareCodexTools } from "./codex-tools.js";
import { registerCodexResourceTools } from "./codex-resource-tools.js";
import { loadAgentManifest } from "./manifest.js";
import { assembleSystemPrompt } from "./prompt-assembly.js";
import { buildAgentResources, type ResourceWatchCallbacks } from "./resources.js";

export type { ResourceWatchCallbacks };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Configuration for creating an agent session.
 *
 * Controls the driver backend, LLM provider and model, agent identity,
 * workspace layout, resource lifecycle, and protocol v2 capability hooks.
 *
 * @docLink packages/runner/dev-guide#flow-execution-turn-based-model
 */
export interface AgentSessionConfig {
  /** Trusted programmatic admission only; wire feature advertisement remains independently gated. */
  managedCodex?: {
    runtime: Omit<CodexManagedRuntimeOptions, "prepareTools">;
    approvals: import("./codex-tools.js").CodexNativeApprovalHandlers;
    requiredTools: readonly string[];
    approvalPolicy: NonNullable<CodexDriverOptions["approvalPolicy"]>;
    sandboxMode: Exclude<NonNullable<CodexDriverOptions["sandboxMode"]>, "danger-full-access">;
    reasoningEffort: NonNullable<CodexDriverOptions["reasoningEffort"]>;
    /** Managed subprompt tier mapping and authoritative models available to this account. */
    subpromptModels?: {
      small: string;
      deep: string;
      available: readonly string[];
    };
  };
  projectDir: string;
  /**
   * Agent working directory — where the agent reads/writes files.
   * Defaults to `projectDir` when omitted. Use this to separate the config
   * root (`projectDir` with `skaile.yaml`) from the actual working directory
   * (e.g. when the user is in a subdirectory or has no `skaile.yaml` in their CWD).
   */
  cwd?: string;
  agentDir?: string;
  driver?: string;
  model?: string;
  provider?: string;
  /**
   * AI cloud transport for claude-sdk (`default|bedrock|vertex|azure|gateway`).
   * Explicit value wins over the settings cascade (skaile.yaml
   * `agent-config.default.cloud`); absent everywhere = `default`.
   *
   * @since 1.3.0
   */
  cloud?: string;
  /**
   * Non-secret cloud transport settings (camelCase). Explicit value wins over
   * the settings cascade. Must stay field-identical with the bridge's
   * `CloudConfig` / core's `CloudTransportConfig` (kept inline to avoid
   * widening the runner↔bridge type surface).
   *
   * @since 1.3.0
   */
  cloudConfig?: { region?: string; projectId?: string; resource?: string; baseUrl?: string };
  /**
   * Resolved credential env bundle for the cloud. serve.ts injects the
   * platform-resolved bundle (from `session_init` secrets, with the Vertex SA
   * JSON already materialized to a file path); CLI/standalone paths leave it
   * unset and the driver falls back to ambient `process.env` credentials.
   * Values are secrets: never logged.
   *
   * @since 1.3.0
   */
  cloudSecrets?: Record<string, string>;
  promptsDir?: string;
  projectClaudeMd?: string;
  sessionId?: string;
  resumeSessionId?: string;
  /**
   * Path to a file whose contents are prepended to the system prompt.
   * @deprecated Use `agent.prompt-extensions` in skaile.yaml instead. The runner
   * no longer injects this file's contents.
   */
  externalPromptFile?: string;
  /**
   * Pre-resolved workspace config. Avoids re-reading skaile.yaml when the caller
   * already has it. Optional — falls back to `resolveSkWorkspaceConfig(projectDir)`.
   */
  wsConfig?: SkWorkspaceConfig;
  /**
   * Platform-level maxTurns override. Takes precedence over manifest.runtime.max_turns.
   * Flow/REPL modes omit this field — they use the manifest value only.
   */
  maxTurns?: number;
  /** Thinking mode override. */
  thinking?: "adaptive" | "enabled" | "disabled";
  /** Reasoning effort level override. */
  effort?: "low" | "medium" | "high" | "max";
  /** Watch callbacks for filesystem/sync events (serve mode only). */
  watch?: ResourceWatchCallbacks;
  /**
   * When true, rclone-backed filesystem-face connectors are mounted in the
   * background instead of awaited, so a slow remote can't delay session
   * bootstrap (notably the platform's `session_init_ack` deadline). Serve mode
   * sets this; CLI/flow/REPL paths leave it unset (mount eagerly).
   */
  deferFilesystemMounts?: boolean;
  /** Secret provider for connector credential resolution. */
  secretProvider?: unknown;
  /**
   * Pre-resolved API key for the active provider. When supplied, it takes
   * precedence over the env-var lookup performed inside `resolveApiKey()`.
   * Used by `serve.ts` to feed the value from `provision_secrets` straight
   * into `AgentConfig.apiKeys` without writing it to `process.env`.
   */
  apiKey?: string;
  /**
   * Optional callback used by `auth: backend` git connectors to request
   * short-lived access tokens from the platform's credential mediator.
   * Threaded into the `ConnectorManager` constructor so all git connectors in
   * this session share the same mediator.
   */
  tokenMediator?: import("@skaile/workspaces/connectors").TokenMediator;
  /**
   * Pre-minted credential provider populated from the v3 `session_init`
   * envelope's `credentials` map. Threaded into ConnectorManager so that
   * connectors with `auth: backend` find their token in-process instead of
   * paying a `request_access_token` round-trip at mount time.
   *
   * @since 3.0.0
   */
  preMintedSecrets?: import("@skaile/workspaces/connectors").PreMintedSecretProvider;
  /**
   * Connector declarations used ONLY when the disk (`skaile.yaml` +
   * materialized assets) yields zero declarations. Serve mode maps the v3
   * `session_init` resolvedConfig through `resolvedConfigToDeclarations` and
   * passes it here so broker-mode sessions (workspace-only bind, no yaml on
   * disk) still get their mounts/connectors. Disk-first: any disk declaration
   * disables the fallback, keeping docker-mode sessions byte-identical.
   * CLI/flow/REPL callers omit this field.
   */
  fallbackConnectorDeclarations?: import("@skaile/workspaces/connectors").ConnectorDeclaration[];
  /**
   * Returns the MCP servers attached at runtime (`runner.attach_instance`) that
   * `skaile.yaml` does not declare, so a session rebuild respawns them instead
   * of dropping them as undeclared "ghosts". A thunk, not an array: serve builds
   * its `AgentSessionConfig` once and reuses it for every recreate, so a snapshot
   * taken at build time would never see a later attach. A disk declaration of the
   * same id wins. CLI/flow/REPL callers omit this field.
   */
  getExtraMcpDeclarations?: () => import("@skaile/workspaces/core").McpServerDeclaration[];
  /**
   * Platform `AIProviderConfig.id` for the AI credential provisioned into
   * this session. Threaded into `AgentConfig.aiProviderConfigId` so that
   * driver recreates (tier-1 native SDK resume, subagents, etc.) preserve
   * the link to the platform mediator. The driver itself does not consume
   * this field; only the runner's 401-mediation handler reads it.
   */
  aiProviderConfigId?: string;
  /**
   * Optional auth-refresh callback threaded into the bridge driver. Invoked
   * by the driver when the underlying agent surfaces an
   * `authentication_error`; in v3 the runner mediates a fresh credential
   * via the `host.refresh_credential` capability and returns a typed
   * {@link CredentialMint}. The driver branches on the discriminator:
   * `ok: true` triggers a one-shot retry of the in-flight prompt;
   * `ok: false` surfaces the original auth error to the caller.
   *
   * Centralising auth-retry inside the driver removes the need to wrap
   * every `driver.prompt(...)` call site (compaction, flow turns, serve
   * handler) with a runner-side retry helper. Standalone CLI / forge
   * sessions leave it undefined.
   *
   * Spec: `_devlog/specs/2026-05-10-deterministic-session-bootstrap.md`
   * § "Refresh-on-401 routes through host.refresh_credential".
   */
  onAuthError?: (args: {
    configId: string;
  }) => Promise<import("@skaile/workspaces/types").CredentialMint>;
  /**
   * Optional callback for the driver's seat-failover path: a turn blocked by an
   * upstream usage limit (not a bad credential). The runner asks the platform
   * to re-resolve the AI provider config and reports whether the session moved
   * to a different one, which the driver acts on by respawning its CLI and
   * replaying the prompt once. Standalone CLI / forge sessions leave it
   * undefined and the limit error surfaces unchanged.
   *
   * @see AgentConfig.onLimitBlocked
   */
  onLimitBlocked?: (
    ctx: import("@skaile/workspaces/bridge").LimitBlockedContext,
  ) => Promise<import("@skaile/workspaces/bridge").LimitFailoverResult>;
  /**
   * Optional fire-and-forget hook for observed AI-provider 401 / 429 responses,
   * forwarded to the platform as `provider_response_seen` for per-seat health
   * attribution.
   *
   * @see AgentConfig.onProviderResponse
   */
  onProviderResponse?: (
    observation: import("@skaile/workspaces/bridge").ProviderResponseObservation,
  ) => void;
  /**
   * Additional sections to append to the assembled system prompt.
   * Used by serve mode to inject the component catalog (AVAILABLE_COMPONENTS)
   * without modifying the session builder's core assembly logic.
   */
  additionalPromptSections?: string[];
  onLog?: (line: string) => void;
  /** Telemetry provider for observability. Optional — omit to disable tracing. */
  telemetry?: TelemetryProvider;
  /** Active trace to attach to this session's driver. */
  trace?: Trace;
  /**
   * Protocol v2 capability dispatch hooks built from the runner's
   * CapabilityRegistry. When present, the driver routes registered tool
   * calls through `hooks.invoke()` instead of the legacy in-driver dispatch.
   *
   * @since 2.0.0
   */
  capabilities?: BridgeCapabilityHooks;
  /**
   * The session's Protocol v2 capability registry. When supplied, external
   * (declarative / recipe-backed) MCP servers are spawned and connected BY THE
   * RUNNER during `buildAgentResources`, with each tool registered into this
   * registry as an `mcp`-origin capability — the bridge-agnostic surface every
   * driver consumes. Omit (CLI / flow) to fall back to the legacy
   * claude-sdk-only SDK delegation for external stdio servers.
   *
   * @since 3.5.0
   */
  capabilityRegistry?: CapabilityRegistry;
}

/**
 * A configured agent session returned by {@link createAgentSession}.
 *
 * Carries a driver instance (not yet started), connected resource and
 * mount managers, resolved runtime assets, and the parsed agent manifest.
 *
 * @docLink packages/runner/dev-guide#flow-execution-turn-based-model
 */
export interface AgentSession {
  /** Driver configured but NOT started. Caller must call driver.start(). */
  driver: AgentDriver;
  /** Connected ConnectorManager — null if no connectors declared. any intentional (optional dep). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resourceManager: any | null;
  /**
   * Resolved runtime assets — catalog entries, implicit refs, npm deps,
   * warnings, plus refresh() / findEntry() helpers. Live-update paths
   * (add_resource, configure) hold this reference and call refresh() when
   * a new repository is cloned or driver installed mid-session.
   */
  runtimeAssets: RuntimeAssetsResult | null;
  /**
   * Resolved composition — inline blocks (already injected into systemPrompt)
   * and discoverable entries (caller registers these with CapabilityRegistry).
   * Null when the agent has no `composes:` field.
   */
  composition: ResolvedComposition | null;
  /** Parsed agent.yaml manifest — model, maxTurns, tools, name. */
  manifest: AgentManifest | undefined;
  /** Active driver type string ('omp' | 'claude-sdk'). */
  driverType: string;
  provider: string | undefined;
  model: string | undefined;
  /** Disconnect all connectors, unmount all mounts, and stop watchers. */
  dispose: () => Promise<void>;
  /** Start filesystem and connector watchers. Call after transport is listening. */
  startWatching: () => void;
  /**
   * Lazily construct (once) and return the session's external MCP manager —
   * the live target for `runner.add_mcp_server`. Serve mode only; throws when
   * no capability registry was supplied (CLI/flow).
   */
  getOrCreateExternalMcpManager: () => import("./external-mcp.js").ExternalMcpManager;
  /** The external MCP manager if constructed (boot or lazy), else null. */
  getExternalMcpManager: () => import("./external-mcp.js").ExternalMcpManager | null;
  /** Telemetry provider for this session (noop if not configured). */
  telemetry?: TelemetryProvider;
  /**
   * Labeled per-section breakdown of the system prompt, in the same
   * order assembleSystemPrompt consumed them. Multi-entry sections
   * (prompt-extensions, composition, additional) appear as repeated
   * entries with `source` set.
   *
   * Consumed by serve.ts's emitSystemPromptComposed helper to emit
   * `system_prompt_composed` events to the platform.
   * @since 2026-05
   */
  systemPromptSections: SystemPromptSection[];
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create an agent session by resolving settings, loading the agent
 * manifest, building resources (connectors, mounts, MCP servers),
 * and creating the driver.
 *
 * The returned driver is NOT started — the caller must call
 * `driver.start()` after optionally attaching event handlers.
 *
 * @param config - Session configuration
 * @returns A fully configured AgentSession ready for start()
 * @docLink packages/runner/dev-guide#flow-execution-turn-based-model
 */
export async function createAgentSession(config: AgentSessionConfig): Promise<AgentSession> {
  const managed = config.managedCodex;
  if (
    managed &&
    (config.driver !== "codex" ||
      config.provider !== "openai" ||
      !config.model ||
      !config.aiProviderConfigId ||
      !config.capabilityRegistry ||
      !config.capabilities ||
      config.apiKey ||
      (config.cloud && config.cloud !== "default") ||
      config.cloudConfig ||
      config.cloudSecrets)
  )
    throw new Error("AI_PROFILE_INVALID");
  const {
    projectDir,
    cwd: cwdOverride,
    agentDir,
    driver: driverOverride,
    model: modelOverride,
    provider: providerOverride,
    sessionId,
    resumeSessionId,
    externalPromptFile,
    wsConfig: wsConfigOverride,
    maxTurns: maxTurnsOverride,
    watch,
    secretProvider,
    onLog,
    telemetry,
    trace,
  } = config;

  const managedWorkspaceConfig = managed
    ? (wsConfigOverride ?? resolveSkWorkspaceConfig(projectDir))
    : undefined;
  if (managedWorkspaceConfig?.agent?.["prompt-extensions"]?.length)
    throw new Error("CODEX_MANAGED_PROMPT_EXTENSIONS_UNSUPPORTED");

  // Agent working directory — defaults to projectDir when not explicitly set.
  const agentCwd = cwdOverride ?? projectDir;
  if (managed)
    prepareNativePolicy({
      cwd: agentCwd,
      privateRoot: managed.runtime.privateRoot,
      privateDirectory: managed.runtime.privateDirectory,
      sandboxMode: managed.sandboxMode,
    });

  const sessionLog = createLogger({
    kind: "runner",
    subkind: "session",
    instance: sessionId ?? "no-session",
  });

  // Step 1: resolve driver/model/provider/apiKey
  const settings = await resolveSettings(projectDir, {
    driver: driverOverride,
    provider: providerOverride,
    model: modelOverride,
  });
  const driverType = managed ? "codex" : (settings.driver ?? "omp");
  const provider = managed ? config.provider : settings.provider;
  const model = managed ? config.model : settings.model;
  const cloud = managed ? "default" : (config.cloud ?? settings.cloud ?? "default");
  const cloudConfig = managed ? undefined : (config.cloudConfig ?? settings.cloudConfig);
  // Explicit `config.apiKey` wins over the env/settings cascade — used by
  // serve.ts to inject the platform-provisioned key without leaking it into
  // `process.env`. Bedrock/vertex/azure sessions never pull a provider API
  // key (credentials ride `cloudSecrets` as an env bundle); gateway sessions
  // DO pull one — it reaches the driver as its gateway auth fallback.
  const apiKey = managed
    ? undefined
    : cloud === "bedrock" || cloud === "vertex" || cloud === "azure"
      ? config.apiKey
      : (config.apiKey ?? resolveApiKey(provider ?? "anthropic", settings));

  // Step 2: load agent manifest (model, maxTurns, tools, name)
  const manifest = agentDir ? await loadAgentManifest(agentDir) : undefined;

  // Step 3: build resources (mounts + connectors lifecycle)
  const {
    resourceManager,
    resourcePromptSection,
    mcpServers,
    runtimeAssets,
    dispose,
    startWatching,
    getOrCreateExternalMcpManager,
    getExternalMcpManager,
    mcpPromptSection,
  } = await buildAgentResources(
    projectDir,
    driverType,
    onLog,
    watch,
    secretProvider,
    {
      provider,
      model,
      sessionId,
      ...(managed ? { managedCodex: true } : {}),
    },
    config.tokenMediator,
    config.preMintedSecrets,
    config.deferFilesystemMounts,
    config.capabilityRegistry,
    config.fallbackConnectorDeclarations,
    config.getExtraMcpDeclarations?.(),
  );

  // Step 4: resolve workspace config + build context/environment sections + load extensions
  const wsConfig =
    managedWorkspaceConfig ?? wsConfigOverride ?? resolveSkWorkspaceConfig(projectDir);
  const contextSection = buildContextSection(wsConfig.agent?.context);
  // Free-form user-authored prompt — placed immediately after the platform
  // context section. Empty / undefined values are dropped by assembleSystemPrompt
  // (filters falsies via `!!p`) so we skip an explicit guard here.
  const userPromptSection = wsConfig.agent?.prompt?.trim();
  const environmentSection = await buildEnvironmentSection(projectDir);
  const promptExtensions = await loadPromptExtensions(
    wsConfig.agent?.["prompt-extensions"],
    projectDir,
    sessionLog,
  );

  if (externalPromptFile) {
    sessionLog.warn(
      "externalPromptFile is deprecated and no longer injected; use agent.prompt-extensions in skaile.yaml instead",
    );
  }

  // Step 4.5: resolve agent composition (composes: field)
  // Produces inline blocks (injected into system prompt below) and discoverable
  // entries (caller registers these with CapabilityRegistry in serve.ts).
  const composition = await resolveSessionComposition(agentDir, manifest, projectDir, sessionLog);

  // Step 5: assemble systemPrompt — context + prompt + env + resources + extensions + composition + additional
  // Agent identity (SOUL/RULES/knowledge) is NOT included here — it is rendered at install time
  // by the renderers in asset-manager and read natively by each backend driver at runtime.
  // Composition inline blocks ARE included here — they are the v2 equivalent of identity injection
  // for agents that use `composes:` (replaces the legacy persona/rules/knowledge fields).
  // The userPromptSection (free-form `agent.prompt`) sits between context and environment per
  // the platform-agent-prompt design spec (2026-05-13).
  const compositionBlocks = composition?.inlineBlocks ?? [];
  const disposeManagedTools = managed
    ? await registerCodexResourceTools({
        registry: config.capabilityRegistry!,
        manager: resourceManager,
        projectDir,
        model: config.model!,
        privateDirectory: managed.runtime.privateRoot,
      })
    : undefined;
  const disposeSessionResources = async () => {
    try {
      await disposeManagedTools?.();
    } finally {
      await dispose();
    }
  };
  const additionalSections = config.additionalPromptSections ?? [];

  // Labeled per-section breakdown — order matches the assembleSystemPrompt input.
  // assembleSystemPrompt filters falsies; we mirror that here so only non-empty
  // sections appear in the breakdown emitted to the platform.
  const systemPromptSections = buildSystemPromptSections({
    contextSection,
    userPromptSection,
    environmentSection,
    resourcePromptSection,
    mcpPromptSection,
    promptExtensions,
    compositionBlocks,
    additionalSections,
  });

  const systemPrompt = assembleSystemPrompt([
    contextSection,
    userPromptSection,
    environmentSection,
    resourcePromptSection,
    mcpPromptSection,
    ...promptExtensions.map((e) => e.value),
    ...compositionBlocks.map((b) => b.content),
    ...additionalSections,
  ]);

  logAssembledSystemPrompt(systemPrompt, sessionLog);

  // Step 5b: resolve thinking/effort — explicit config wins over workspace config
  const thinking =
    config.thinking ?? (wsConfig.agent_config?.default?.thinking as AgentSessionConfig["thinking"]);
  const effort =
    config.effort ??
    (managed
      ? undefined
      : (wsConfig.agent_config?.default?.effort as AgentSessionConfig["effort"]));

  // Step 6: resolve maxTurns — platform override wins over manifest
  const maxTurns = maxTurnsOverride ?? manifest?.runtime?.max_turns;

  // Step 7: create driver (NOT started)
  // Driver CWD is the agent working directory (may differ from config root).
  let driver: AgentDriver;
  try {
    driver = await createSessionDriver({
      config,
      driverType,
      agentCwd,
      provider,
      model,
      cloud,
      cloudConfig,
      manifest,
      maxTurns,
      apiKey,
      systemPrompt,
      thinking,
      effort,
      sessionId,
      resumeSessionId,
      mcpServers,
      agentDir,
    });
  } catch (error) {
    try {
      await disposeSessionResources();
    } catch (cleanupError) {
      throw new AggregateError([error, cleanupError], "Session creation and cleanup failed", {
        cause: error,
      });
    }
    throw error;
  }

  // Attach telemetry to driver if provided
  if (telemetry && trace) {
    driver.setTelemetry(telemetry, trace);
  }

  return {
    driver,
    resourceManager,
    runtimeAssets,
    composition,
    manifest,
    driverType,
    provider,
    model,
    dispose: disposeSessionResources,
    startWatching,
    getOrCreateExternalMcpManager,
    getExternalMcpManager,
    telemetry,
    systemPromptSections,
  };
}

/** Build a disposable managed Codex child with the parent's resolved authority and fresh state. */
export function createManagedCodexSubpromptDriver(
  config: AgentSessionConfig,
  model: string | undefined,
  cwd: string,
  privateDirectory: string,
): Promise<AgentDriver> {
  const managed = config.managedCodex;
  if (!managed || !model || !config.sessionId || !config.aiProviderConfigId || !config.capabilities)
    return Promise.reject(new Error("AI_PROFILE_INVALID"));
  prepareNativePolicy({
    cwd,
    privateRoot: managed.runtime.privateRoot,
    privateDirectory,
    sandboxMode: managed.sandboxMode,
  });
  return createDriver("codex", {
    cwd,
    provider: "openai",
    model,
    sessionId: `${config.sessionId}:subprompt:${randomUUID()}`,
    maxTurns: 1,
    aiProviderConfigId: config.aiProviderConfigId,
    driverOptions: {
      codex: {
        authSource: "managed",
        approvalPolicy: managed.approvalPolicy,
        sandboxMode: managed.sandboxMode,
        reasoningEffort: managed.reasoningEffort,
        managedRuntime: {
          ...managed.runtime,
          privateDirectory,
          nativeTurnBudget: 1,
          credential: structuredClone(managed.runtime.credential),
          prepareTools: (signal) =>
            prepareCodexTools({
              hooks: config.capabilities!,
              requiredTools: managed.requiredTools,
              approvals: managed.approvals,
              signal,
            }),
        },
      },
    },
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * One loaded prompt-extension file. `source` carries the original relative
 * path from `skaile.yaml` so labeled breakdowns (system_prompt_composed event)
 * can attribute each section to a specific file.
 */
type LoadedPromptExtension = { value: string; source: string };

/**
 * Load markdown files declared under `agent.prompt-extensions` in skaile.yaml.
 * Paths are resolved relative to the project root. Missing or unreadable files
 * are skipped with a warning. Empty files are filtered out.
 *
 * **Async and deadline-bounded since #454.** A `prompt-extensions` entry is a
 * project-relative path, so `workspace/house-style.md` resolves onto the rclone
 * FUSE mount. This runs inside the `session_init` command handler with the
 * WebSocket already live, so the pre-#454 `existsSync` + `readFileSync` could
 * park the whole `skaile serve` event loop on a wedged mount — and a hang is not
 * an error, so the surrounding `try/catch` never fired. The `existsSync` probe is
 * gone too: it was a second stat on the same possibly-wedged path, and a missing
 * file is already indistinguishable from an unreadable one here.
 *
 * Reads run concurrently — one slow extension no longer serialises the rest, and
 * a missing entry never aborts the ones that are present.
 */
async function loadPromptExtensions(
  paths: string[] | undefined,
  projectDir: string,
  log: ReturnType<typeof createLogger>,
): Promise<LoadedPromptExtension[]> {
  if (!paths?.length) return [];
  const loaded = await Promise.all(
    paths.map(async (relPath): Promise<LoadedPromptExtension | null> => {
      const absPath = resolvePath(projectDir, relPath);
      try {
        const content = (
          await withFsDeadline(`readFile ${absPath}`, () => readFile(absPath, "utf-8"))
        ).trim();
        return content.length > 0 ? { value: content, source: relPath } : null;
      } catch (err) {
        log.warn("could not read prompt-extension", {
          relPath,
          error: err instanceof Error ? err.message : String(err),
        });
        return null;
      }
    }),
  );
  return loaded.filter((e): e is LoadedPromptExtension => e !== null);
}

/**
 * Resolve the agent composition (`composes:` field) when an agentDir is
 * available. Returns inline blocks (injected into the system prompt) and
 * discoverable entries (registered with the CapabilityRegistry by the caller).
 * Returns null when the agent has no composition or no agentDir is available.
 */
async function resolveSessionComposition(
  agentDir: string | undefined,
  manifest: AgentManifest | undefined,
  projectDir: string,
  log: ReturnType<typeof createLogger>,
): Promise<ResolvedComposition | null> {
  if (agentDir && manifest?.composes?.length) {
    return resolveAgentComposition({
      agentDir,
      manifest,
      projectDir,
      log,
    });
  }
  if (manifest?.composes?.length && !agentDir) {
    log.debug("skipping composition resolution — no agentDir available");
  }
  return null;
}

/**
 * Build the labeled per-section breakdown of the system prompt. Order matches
 * the assembleSystemPrompt input; assembleSystemPrompt filters falsies, so we
 * mirror that here and only emit non-empty sections.
 */
function buildSystemPromptSections(parts: {
  contextSection: string | undefined;
  userPromptSection: string | undefined;
  environmentSection: string | undefined;
  resourcePromptSection: string | undefined;
  mcpPromptSection: string | undefined;
  promptExtensions: LoadedPromptExtension[];
  compositionBlocks: ResolvedComposition["inlineBlocks"];
  additionalSections: string[];
}): SystemPromptSection[] {
  const {
    contextSection,
    userPromptSection,
    environmentSection,
    resourcePromptSection,
    mcpPromptSection,
    promptExtensions,
    compositionBlocks,
    additionalSections,
  } = parts;
  const systemPromptSections: SystemPromptSection[] = [];
  if (contextSection) systemPromptSections.push({ label: "context", value: contextSection });
  if (userPromptSection) systemPromptSections.push({ label: "prompt", value: userPromptSection });
  if (environmentSection)
    systemPromptSections.push({ label: "environment", value: environmentSection });
  if (resourcePromptSection)
    systemPromptSections.push({ label: "resources", value: resourcePromptSection });
  if (mcpPromptSection)
    systemPromptSections.push({ label: "mcp-guidance", value: mcpPromptSection });
  for (const ext of promptExtensions) {
    systemPromptSections.push({
      label: "prompt-extensions",
      value: ext.value,
      source: ext.source,
    });
  }
  for (const block of compositionBlocks) {
    // InlineBlock (composition/types.ts) carries `ref` as the identifying field
    // (asset ref string like "skill:foo" or "agent:bar"); no separate `id`/`source` field.
    systemPromptSections.push({
      label: "composition",
      value: block.content,
      source: block.ref,
    });
  }
  additionalSections.forEach((value, idx) => {
    // additionalPromptSections is string[] today — no per-entry source label
    // available, so we synthesize a stable index-based descriptor.
    systemPromptSections.push({
      label: "additional",
      value,
      source: `additional[${idx}]`,
    });
  });
  return systemPromptSections;
}

/**
 * Log the assembled system prompt so it can be inspected via container logs.
 * No secrets flow through this string: API keys live in `apiKeys`, connector
 * credentials are resolved by the secret provider. Set SKAILE_LOG_SYSTEM_PROMPT=0
 * to suppress.
 */
function logAssembledSystemPrompt(
  systemPrompt: string | undefined,
  log: ReturnType<typeof createLogger>,
): void {
  if (process.env.SKAILE_LOG_SYSTEM_PROMPT !== "0") {
    const length = systemPrompt?.length ?? 0;
    log.debug("assembled system prompt", { length });
    log.debug("system prompt content", { content: systemPrompt ?? "(empty)" });
  }
}

/**
 * Create the agent driver (NOT started). Driver CWD is the agent working
 * directory (may differ from the config root). Forwards the resolved
 * provider/model/turns/identity plus optional protocol-v2 capability hooks.
 */
function createSessionDriver(args: {
  config: AgentSessionConfig;
  driverType: string;
  agentCwd: string;
  provider: string | undefined;
  model: string | undefined;
  cloud: string;
  cloudConfig: AgentSessionConfig["cloudConfig"];
  manifest: AgentManifest | undefined;
  maxTurns: number | undefined;
  apiKey: string | undefined;
  systemPrompt: string | undefined;
  thinking: AgentSessionConfig["thinking"];
  effort: AgentSessionConfig["effort"];
  sessionId: string | undefined;
  resumeSessionId: string | undefined;
  mcpServers: Record<string, unknown> | undefined;
  agentDir: string | undefined;
}): ReturnType<typeof createDriver> {
  const {
    config,
    driverType,
    agentCwd,
    provider,
    model,
    cloud,
    cloudConfig,
    manifest,
    maxTurns,
    apiKey,
    systemPrompt,
    thinking,
    effort,
    sessionId,
    resumeSessionId,
    mcpServers,
    agentDir,
  } = args;
  return createDriver(driverType, {
    ...(config.managedCodex
      ? {
          driverOptions: {
            codex: {
              authSource: "managed" as const,
              approvalPolicy: config.managedCodex.approvalPolicy,
              sandboxMode: config.managedCodex.sandboxMode,
              reasoningEffort: config.managedCodex.reasoningEffort,
              managedRuntime: {
                ...config.managedCodex.runtime,
                prepareTools: (signal: AbortSignal) =>
                  prepareCodexTools({
                    hooks: config.capabilities!,
                    requiredTools: config.managedCodex!.requiredTools,
                    approvals: config.managedCodex!.approvals,
                    signal,
                  }),
              },
            },
          },
        }
      : {}),
    cwd: agentCwd,
    provider,
    model: model ?? manifest?.model?.preferred,
    maxTurns,
    apiKeys: apiKey ? { [provider ?? "anthropic"]: apiKey } : undefined,
    systemPrompt,
    tools: manifest?.tools,
    // Cloud transport dimension — omitted entirely for `default` so the
    // driver config stays byte-identical to pre-cloud behavior.
    ...(cloud !== "default" ? { cloud } : {}),
    ...(cloudConfig ? { cloudConfig } : {}),
    ...(config.cloudSecrets ? { cloudSecrets: config.cloudSecrets } : {}),
    ...(thinking ? { thinking } : {}),
    ...(effort ? { effort } : {}),
    ...(sessionId ? { sessionId } : {}),
    ...(resumeSessionId ? { resumeSessionId } : {}),
    ...(mcpServers ? { mcpServers } : {}),
    // Protocol v2 capability dispatch hooks. When provided, the driver builds
    // a synthetic `skaile-capabilities` MCP server alongside any existing
    // mcpServers so capability invocations route through the registry's
    // `invoke()` (which logs + validates + handles fire-and-forget / render).
    ...(config.capabilities ? { capabilities: config.capabilities } : {}),
    ...(config.aiProviderConfigId ? { aiProviderConfigId: config.aiProviderConfigId } : {}),
    ...(config.onAuthError ? { onAuthError: config.onAuthError } : {}),
    ...(config.onLimitBlocked ? { onLimitBlocked: config.onLimitBlocked } : {}),
    ...(config.onProviderResponse ? { onProviderResponse: config.onProviderResponse } : {}),
    // Always forward agentDir/agentName for native identity handling
    ...(agentDir ? { agentDir, agentName: manifest?.name } : {}),
  });
}
